import { computed, inject, Service, signal, type Signal } from '@angular/core';
import {
  AppAuthService,
  BrandedLocalStorage,
  TRUEFORM_BRAND,
  type TrueformBrand,
  USERS_API,
} from '@trueform/ui/core';
import { decodeJwtPayload } from '@/core/auth/jwt';
import { BehaviorSubject, catchError, map, Observable, of, shareReplay, tap } from 'rxjs';

const STORAGE_SUFFIX = 'selected_company_code';

/** A selectable company: the DCS `code` plus the label shown to the user. */
export interface Company {
  code: string;
  name: string;
}

/**
 * App-owned store for the DCS selected company (ADR-002 Q1).
 *
 * This is a SELECTOR / FALLBACK store only — the one-way rule is
 * URL → fetch: entity fetchers read the `/catalogs/:companyCode` route param,
 * never this signal. This service exists to answer "which company do the
 * static sidebar links point at?" and to persist the last selection, and to
 * power the global company switcher in the shell.
 *
 * Compliance with [[gotchas]] §1: signals + `inject()` only, `@Service`
 * (auto-provided root singleton), no global `Date`, no `.mutate()`.
 */
@Service()
export class CompanyContextService {
  private readonly usersApi = inject(USERS_API);
  private readonly authService = inject(AppAuthService);
  private readonly brand: TrueformBrand = inject(TRUEFORM_BRAND);
  private readonly storage = new BrandedLocalStorage(this.brand.storagePrefix);

  private readonly companiesState = signal<Company[]>([]);
  private readonly selectedCodeState = signal<string | null>(null);
  private readonly isReadyState = signal(false);

  /** Holds the in-flight load so `ensureLoaded()` dedupes concurrent calls. */
  private readonly inFlight$ = new BehaviorSubject<Observable<Company[]> | null>(null);

  readonly companies: Signal<Company[]> = this.companiesState.asReadonly();
  readonly selectedCode: Signal<string | null> = this.selectedCodeState.asReadonly();
  readonly isReady: Signal<boolean> = this.isReadyState.asReadonly();

  /** Codes-only projection for code-based consumers (route guards). */
  readonly companyCodes: Signal<string[]> = computed(() => this.companies().map((c) => c.code));

  hasCompany(code: string): boolean {
    return this.companies().some((c) => c.code === code);
  }

  select(code: string): void {
    if (!this.hasCompany(code)) {
      return;
    }
    this.selectedCodeState.set(code);
    this.storage.write(STORAGE_SUFFIX, code);
  }

  /**
   * Idempotent per user session: returns the resolved companies, kicking off
   * (or joining) the POS lookup exactly once. Safe to call concurrently.
   */
  ensureLoaded(): Observable<Company[]> {
    if (this.isReady()) {
      return of(this.companies());
    }
    const existing = this.inFlight$.getValue();
    if (existing) {
      return existing;
    }
    const request = this.load().pipe(
      map((codes) => this.toCompanies(codes)),
      tap((companies) => this.applyCompanies(companies)),
      shareReplay({ bufferSize: 1, refCount: false }),
    );
    this.inFlight$.next(request);
    return request;
  }

  /**
   * Companies are exactly what POS returns — no synthetic entries. POS
   * `/companies` codes carry no display name, so each code is used as its own
   * label. De-duplicated defensively (POS should not repeat a code).
   */
  private toCompanies(codes: string[]): Company[] {
    const seen = new Set<string>();
    const companies: Company[] = [];
    for (const code of codes) {
      if (!seen.has(code)) {
        seen.add(code);
        companies.push({ code, name: code });
      }
    }
    return companies;
  }

  private load(): Observable<string[]> {
    const userCode = this.userCode();
    if (!userCode) {
      return of([]);
    }
    return this.usersApi.companies(userCode).pipe(
      map((resp) => resp?.company_codes ?? []),
      catchError(() => of([])),
    );
  }

  private applyCompanies(companies: Company[]): void {
    this.inFlight$.next(null);
    this.companiesState.set(companies);

    // A still-valid persisted selection wins; otherwise default to the first
    // fetched company (null when POS returned none).
    const stored = this.storage.read(STORAGE_SUFFIX);
    const storedValid = stored !== null && this.hasCompany(stored);
    this.selectedCodeState.set(storedValid ? stored : (companies[0]?.code ?? null));

    if (stored !== null && !storedValid) {
      this.storage.remove(STORAGE_SUFFIX);
    }
    this.isReadyState.set(true);
  }

  /**
   * Session user → POS `user_code` mapping (resolves TASK-002's Open
   * Question). The backend projects the POS `user_code` into the Stytch
   * session JWT's `external_id` claim, so the code is read from the JWT —
   * never from `AppUser.user_id`, which is the Stytch member id the POS does
   * not recognize. The backend guarantees the claim on every session JWT;
   * when it is absent (or no session token exists) there is no fallback —
   * `load()` degrades to an empty company list.
   */
  private userCode(): string | null {
    return decodeJwtPayload(this.authService.getSessionToken())?.external_id ?? null;
  }
}
